import { useRef, useState } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { QUESTION_TYPES, SURVEY_TYPES } from '@api/instant_survey';
import { maxQuestionError } from '@api/surveys';
import { IconButton, OverlayPopover, PlusIcon } from '@utilities';
import { GATES } from '@constants';

import { useSurveyContext } from '../../../../utilities/context';

import styles from './_index.module.scss';

export const ADD_NUMERATOR_SCREENERS_LIBRARY_LABEL = 'Add Standard Screener';
const MAX_SCREENER_QUESTIONS = 2;

interface AddQuestionIconButtonProps {
  className?: string;
  onAddAttentionCheck: () => void;
  onAddMonadicSection?: () => void;
  onAddQuestion: () => void;
  onCreateQuestion: () => void;
  onCreateScreenerQuestion: () => void;
}

const AddQuestionIconButton = ({
  className,
  onAddAttentionCheck,
  onAddMonadicSection,
  onAddQuestion,
  onCreateQuestion,
  onCreateScreenerQuestion,
}: AddQuestionIconButtonProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { client } = useStatsigClient();

  const hasQuestionLibrary = client.checkGate(GATES.QUESTION_LIBRARY);
  const canAddAttentionCheck = client.checkGate(GATES.ATTENTION_CHECK);
  const canCreateScreenerQuestion = client.checkGate(GATES.SCREENER_QUESTIONS);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const { activeQuestions, currentQuestionCount, instantSurveyType, maxQuestions } =
    useSurveyContext();
  const isSequentialMonadicSurvey = instantSurveyType?.id === SURVEY_TYPES.SEQUENTIAL_MONADIC;
  const hasVideoQuestion = activeQuestions.some((q) => q.type === QUESTION_TYPES.VIDEO.value);
  const screenerQuestionCount = activeQuestions.filter((question) => question.isScreener).length;
  const canAddQuestion = currentQuestionCount < maxQuestions;
  const canAddScreenerQuestion = screenerQuestionCount < MAX_SCREENER_QUESTIONS;
  const hasDropdownOptions =
    canCreateScreenerQuestion ||
    hasQuestionLibrary ||
    isSequentialMonadicSurvey ||
    canAddAttentionCheck;

  const handleOnAdd = () => {
    setIsPopoverOpen(false);
    onAddQuestion();
  };
  const handleOnCreate = () => {
    setIsPopoverOpen(false);
    onCreateQuestion();
  };
  const handleOnCreateScreenerQuestion = () => {
    if (!canAddScreenerQuestion) return;

    setIsPopoverOpen(false);
    onCreateScreenerQuestion();
  };
  const handleOnCreateMonadicSection = () => {
    onAddMonadicSection?.();
    setIsPopoverOpen(false);
  };
  const handleOnAddAttentionCheck = () => {
    setIsPopoverOpen(false);
    onAddAttentionCheck();
  };

  return (
    <>
      <IconButton
        className={className}
        isDisabled={!canAddQuestion}
        onClick={hasDropdownOptions ? () => setIsPopoverOpen(true) : handleOnCreate}
        ref={buttonRef}
        tooltip={canAddQuestion ? 'Add Question' : maxQuestionError}
      >
        <PlusIcon />
      </IconButton>
      {hasDropdownOptions && (
        <OverlayPopover
          className={styles['add-question-icon-button-dropdown']}
          isActive={isPopoverOpen}
          onHide={() => setIsPopoverOpen(false)}
          target={buttonRef.current}
        >
          <ul>
            <li>
              <button disabled={hasVideoQuestion} onClick={handleOnCreate}>
                <span>Create Question</span>
                <PlusIcon />
              </button>
            </li>
            {hasQuestionLibrary && (
              <li>
                <button disabled={hasVideoQuestion} onClick={handleOnAdd}>
                  <span>Add From Library</span>
                  <PlusIcon />
                </button>
              </li>
            )}
            {canCreateScreenerQuestion && (
              <li>
                <button disabled={!canAddScreenerQuestion} onClick={handleOnCreateScreenerQuestion}>
                  <span>{ADD_NUMERATOR_SCREENERS_LIBRARY_LABEL}</span>
                  <PlusIcon />
                </button>
              </li>
            )}
            {canAddAttentionCheck && (
              <li>
                <button onClick={handleOnAddAttentionCheck}>
                  <span>Add Attention Check</span>
                  <PlusIcon />
                </button>
              </li>
            )}
            {isSequentialMonadicSurvey && (
              <li>
                <button onClick={handleOnCreateMonadicSection}>
                  <span>Create Monadic Section</span>
                  <PlusIcon />
                </button>
              </li>
            )}
          </ul>
        </OverlayPopover>
      )}
    </>
  );
};

export default AddQuestionIconButton;
